// frontend/src/pages/ManageLeadEmails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getAdminLeadById, 
  updateLead, 
  getLocationFromZip,
  generatePaymentUrl
} from '../api';

// Import the email API functions
import {
  getAgentsByState,
  sendInitialLeadEmail,
  getEmailPreview,
  getLeadEmailHistory,
  notifyAllAgentsInState
} from '../api/emailApi';

// Import components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

// Import icons
import { 
  MapPin, MailOpen, Send, User, Users, Home, Mail, 
  ChevronDown, ChevronUp, ExternalLink, Copy, Check, 
  ArrowLeft
} from 'lucide-react';

// Import constants
import { US_STATES } from '../constants';

const ManageLeadEmails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [lead, setLead] = useState(null);
  const [agents, setAgents] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showEmailHistoryModal, setShowEmailHistoryModal] = useState(false);
  const [sendingToAllAgents, setSendingToAllAgents] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    payment: true,
    agents: false,
    emailHistory: false
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  
  // Fetch agents for a state
  const fetchAgents = useCallback(async (state) => {
    if (!state) {
      console.log('No state provided, skipping agent fetch');
      return;
    }
    
    setLoadingAgents(true);
    try {
      console.log(`Fetching agents for state: ${state}`);
      // Ensure state is properly formatted (uppercase)
      const normalizedState = state.toUpperCase();
      
      const result = await getAgentsByState(normalizedState);
      console.log('Agent fetch result:', result);
      
      if (result.success) {
        setAgents(result.data || []);
        console.log(`Fetched ${result.data?.length || 0} agents for state ${normalizedState}`);
      } else {
        console.error('Failed to fetch agents:', result.message);
        setError(`Failed to fetch agents for this state: ${result.message}`);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Error fetching agents. Please try again.');
    } finally {
      setLoadingAgents(false);
    }
  }, []);
  
  // Fetch location data from zip code
  const fetchLocationData = useCallback(async (zipCode) => {
    // Validate ZIP code before making API call
    if (!zipCode || zipCode === 'Unknown' || zipCode.length < 5 || !/^\d{5}$/.test(zipCode)) {
      console.log('Invalid ZIP code, skipping location lookup:', zipCode);
      return;
    }
    
    setLoadingPreview(true);
    try {
      console.log(`Fetching location data for ZIP: ${zipCode}`);
      const result = await getLocationFromZip(zipCode);
      console.log('Location data result:', result);
      
      if (result.success) {
        const { city, state } = result.data;
        
        // Update the lead with city and state
        if (city || state) {
          const updateData = {};
          if (city) updateData.city = city;
          if (state) updateData.state = state;
          
          console.log('Updating lead with location data:', updateData);
          
          // Update lead in database
          const updateResult = await updateLead(id, updateData);
          if (updateResult.success) {
            setLead(prev => ({
              ...prev,
              ...updateData
            }));
            setSuccess('Updated lead with location data');
            
            // If we got a state, fetch agents for it
            if (state) {
              fetchAgents(state);
            }
          } else {
            console.error('Failed to update lead with location data:', updateResult.message);
          }
        }
      } else {
        console.error('Failed to fetch location data:', result.message);
      }
    } catch (err) {
      console.error('Error fetching location data:', err);
    } finally {
      setLoadingPreview(false);
    }
  }, [id, fetchAgents]);
  
  // Generate payment link
  const handleGeneratePaymentLink = useCallback(async () => {
    if (!lead) {
      console.log('No lead data, cannot generate payment link');
      return;
    }
    
    try {
      setGeneratingLink(true);
      console.log(`Generating payment link for lead ${lead.id}`);
      
      const response = await generatePaymentUrl(lead.id);
      console.log('Payment URL generation response:', response);
      
      if (response.success) {
        setSuccess('Payment link generated successfully');
        
        // Update the lead state with the new payment link
        setLead(prevLead => ({
          ...prevLead,
          stripe_payment_link: response.data?.payment_link
        }));
      } else {
        setError(`Failed to generate payment link: ${response.message}`);
      }
    } catch (err) {
      console.error('Error generating payment link:', err);
      setError('Failed to generate payment link');
    } finally {
      setGeneratingLink(false);
    }
  }, [lead]);
  
  // Fetch lead data
  const fetchLead = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log("Fetching lead data for ID:", id);
      const result = await getAdminLeadById(id);
      console.log('Lead data result:', result);
      
      if (result.success) {
        // Ensure state is uppercase if present
        if (result.data.state) {
          result.data.state = result.data.state.toUpperCase();
        }
        
        setLead(result.data);
        
        // If lead has a state, fetch agents for that state
        if (result.data.state) {
          console.log(`Lead has state ${result.data.state}, fetching agents`);
          fetchAgents(result.data.state);
        } 
        // If no city but has zip code, try to get location data
        else if (!result.data.city && result.data.zip_code) {
          console.log(`Lead has ZIP code ${result.data.zip_code} but no state, fetching location data`);
          fetchLocationData(result.data.zip_code);
        }
  
        // If lead doesn't have a payment link, generate one
        if (result.data && !result.data.stripe_payment_link) {
          try {
            setGeneratingLink(true);
            console.log(`Lead ${result.data.id} has no payment link, generating one`);
            
            const paymentResponse = await generatePaymentUrl(result.data.id);
            if (paymentResponse.success) {
              setSuccess('Payment link generated successfully');
              
              // Update the lead state with the new payment link
              setLead(prevLead => ({
                ...prevLead,
                stripe_payment_link: paymentResponse.data?.payment_link
              }));
            } else {
              setError(`Failed to generate payment link: ${paymentResponse.message}`);
            }
          } catch (err) {
            console.error('Error generating payment link:', err);
            setError('Failed to generate payment link');
          } finally {
            setGeneratingLink(false);
          }
        }
      } else {
        setError(result.message || 'Failed to fetch lead data.');
      }
    } catch (err) {
      console.error('Error fetching lead data:', err);
      setError('Failed to fetch lead data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, fetchLocationData, fetchAgents]);
  
  // Fetch email history
  const fetchEmailHistory = useCallback(async () => {
    if (!id) return;
    
    setLoadingHistory(true);
    setError('');
    try {
      console.log(`Fetching email history for lead ${id}`);
      const result = await getLeadEmailHistory(id);
      console.log('Email history result:', result);
      
      if (result.success) {
        // Sort by sent_at date, newest first
        const sortedHistory = (result.data || []).sort((a, b) => {
          return new Date(b.sent_at) - new Date(a.sent_at);
        });
        
        setEmailHistory(sortedHistory);
        console.log(`Fetched ${sortedHistory.length} email history records`);
      } else {
        setError('Failed to load email history: ' + (result.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching email history:', err);
      setError('Failed to load email history. Please try again.');
    } finally {
      setLoadingHistory(false);
    }
  }, [id]);
  
  // Load initial data
  useEffect(() => {
    if (id) {
      fetchLead();
      fetchEmailHistory();
    }
  }, [id, fetchLead, fetchEmailHistory]);
  
  // Load email history when section expands
  useEffect(() => {
    if (expandedSections.emailHistory && emailHistory.length === 0) {
      fetchEmailHistory();
    }
  }, [expandedSections.emailHistory, emailHistory.length, fetchEmailHistory]);
  
  // Preview email
  const handleEmailPreview = async () => {
    try {
      setLoadingPreview(true);
      setError('');
      
      console.log(`Previewing email for lead ${id}`);
      const result = await getEmailPreview('initial', id);
      console.log('Email preview result:', result);
      
      if (result.success && result.data && result.data.html) {
        setEmailPreviewHtml(result.data.html);
        setShowEmailPreviewModal(true);
      } else {
        setError(result.message || 'Failed to generate email preview');
      }
    } catch (err) {
      console.error('Error generating email preview:', err);
      setError('Error generating email preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Send email to all agents
  const handleSendToAllAgents = async () => {
    if (!lead?.state || sendingToAllAgents) return;
    
    try {
      setSendingToAllAgents(true);
      setError('');
      
      console.log(`Sending email to all agents in ${lead.state} for lead ${lead.id}`);
      const result = await notifyAllAgentsInState(lead.id, lead.state);
      console.log('Send to all agents result:', result);
      
      if (result.success) {
        setSuccess(`Successfully sent emails to ${result.data?.sentCount || 'all'} agents in ${lead.state}`);
        setShowEmailPreviewModal(false);
        // Refresh email history
        await fetchEmailHistory();
        setExpandedSections(prev => ({ ...prev, emailHistory: true }));
      } else {
        setError(result.message || 'Failed to send emails');
      }
    } catch (err) {
      console.error('Error sending emails to all agents:', err);
      setError('Error sending emails. Please try again.');
    } finally {
      setSendingToAllAgents(false);
    }
  };
  
  // Send email to a single agent
  const handleSendEmail = async (agentEmail) => {
    try {
      setSendingEmails(true);
      setError('');
      
      console.log(`Sending email to ${agentEmail} for lead ${id}`);
      const result = await sendInitialLeadEmail(id, agentEmail);
      console.log('Send email result:', result);
      
      if (result.success) {
        setSuccess(`Successfully sent email to ${agentEmail}`);
        // Refresh email history
        await fetchEmailHistory();
      } else {
        setError(result.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Error sending email. Please try again.');
    } finally {
      setSendingEmails(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ 
      ...prev, 
      [section]: !prev[section] 
    }));
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
          setSuccess('Copied to clipboard');
        })
        .catch(err => {
          console.error('Could not copy text: ', err);
          setError('Failed to copy to clipboard');
        });
    }
  };

  // Calculate age from year born if available
  const calculateAge = () => {
    if (lead?.year_born) {
      const currentYear = new Date().getFullYear();
      return currentYear - parseInt(lead.year_born);
    }
    return lead?.age || 'Unknown';
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner className="h-12 w-12 text-border-primary" />
        <p className="mt-4 text-text-secondary">Loading lead information...</p>
      </div>
    );
  }
  
  // Error state
  if (!lead) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertTitle>Lead Not Found</AlertTitle>
        <AlertDescription>
          {error || 'The requested lead information could not be found.'}
          <div className="mt-4">
            <Button onClick={() => navigate('/admin/leads')}>
              Back to Leads
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }
  
  // Get state name
  const stateName = lead.state ? (US_STATES[lead.state] || lead.state) : 'Unknown';
  
  return (
    <div className="container mx-auto py-6 space-y-6 max-w-7xl">
      {/* Header with lead info */}
      <div className="bg-background-secondary rounded-lg p-6 border border-border-primary shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">Lead #{id}</h1>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-status-info-bg text-status-info-text">
                <MapPin className="h-4 w-4 mr-1" />
                {stateName}
              </span>
              {lead.city && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-status-success-bg text-status-success-text">
                  <Home className="h-4 w-4 mr-1" />
                  {lead.city}
                </span>
              )}
              {lead.is_purchased ? (
                <Badge variant="success" className="text-sm py-1 px-3">Purchased</Badge>
              ) : (
                <Badge variant="secondary" className="text-sm py-1 px-3">Available</Badge>
              )}
            </div>
            <p className="text-text-secondary">
              {lead?.name || 'Unknown'} • {lead?.email || 'No Email'} • Created on{' '}
              {lead?.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/leads')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to All Leads
          </Button>
        </div>
      </div>
      
      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Quick Actions
          </CardTitle>
          <CardDescription>
            Manage communications for this lead
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              className="w-full"
              size="lg"
              disabled={!lead?.state || loadingAgents}
              onClick={handleEmailPreview}
            >
              {loadingAgents ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Loading Agents...
                </>
              ) : agents.length > 0 ? (
                <>
                  <MailOpen className="mr-2 h-5 w-5" />
                  Preview & Send Email ({agents.length} Agent{agents.length === 1 ? '' : 's'})
                </>
              ) : (
                <>
                  <MailOpen className="mr-2 h-5 w-5" />
                  Preview & Send Email
                </>
              )}
            </Button>
            
            <Button
              className="w-full"
              variant="outline"
              size="lg"
              onClick={() => {
                fetchEmailHistory();
                setShowEmailHistoryModal(true);
              }}
            >
              <Mail className="mr-2 h-5 w-5" />
              View Email History
            </Button>
            
            <Button
              className="w-full"
              variant="outline"
              size="lg"
              onClick={() => setShowAgentsModal(true)}
              disabled={!lead?.state || agents.length === 0}
            >
              <Users className="mr-2 h-5 w-5" />
              View Agents ({agents.length})
            </Button>
          </div>
          
          {/* Email Status Summary Card */}
          <div className="mt-4 p-3 bg-background-tertiary rounded-md border border-border-primary">
            <h3 className="text-sm font-medium mb-2 text-text-secondary">Email Status Summary</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="text-xs text-text-tertiary block">Initial Emails</span>
                <Badge variant="secondary" className="mt-1">
                  {emailHistory.filter(email => email.email_type === 'initial_notification').length}
                </Badge>
              </div>
              <div>
                <span className="text-xs text-text-tertiary block">Full Details</span>
                <Badge variant="success" className="mt-1">
                  {emailHistory.filter(email => email.email_type === 'full_details').length}
                </Badge>
              </div>
              <div>
                <span className="text-xs text-text-tertiary block">Status</span>
                {lead?.is_purchased ? (
                  <Badge variant="success" className="mt-1">Purchased</Badge>
                ) : (
                  <Badge variant="default" className="mt-1">Available</Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Expandable Sections */}
      
      {/* Lead Details Section */}
      <Card>
        <CardHeader 
          className={`pb-3 cursor-pointer hover:bg-background-tertiary ${expandedSections.details ? '' : 'border-b'}`}
          onClick={() => toggleSection('details')}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-border-primary" />
              Lead Information
            </CardTitle>
            {expandedSections.details ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        
        {expandedSections.details && (
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Location Information */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-text-secondary">Location</h3>
                <div className="space-y-1">
                  {lead.city && (
                    <p className="font-medium">City: <span className="text-text-primary">{lead.city}</span></p>
                  )}
                  <p className="font-medium">State: <span className="text-text-primary">{stateName}</span></p>
                  <p className="font-medium">ZIP: <span className="text-text-primary">{lead.zip_code || 'N/A'}</span></p>
                </div>
              </div>
              
              {/* Age Information */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-text-secondary">Age Information</h3>
                <div className="space-y-1">
                  {lead.year_born && (
                    <p className="font-medium">Year Born: <span className="text-text-primary">{lead.year_born}</span></p>
                  )}
                  <p className="font-medium">Age: <span className="text-text-primary">{calculateAge()}</span></p>
                </div>
              </div>
              
              {/* Investment Information */}
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-text-secondary">Investment Amount</h3>
                <p className="text-2xl font-bold text-text-primary">
                  {lead.investment_amount
                    ? typeof lead.investment_amount === 'string' && lead.investment_amount.includes('$')
                      ? lead.investment_amount
                      : formatCurrency(lead.investment_amount)
                    : 'Not specified'}
                </p>
                <p className="text-sm text-text-secondary">{lead.retirement_timeline || 'Timeline not specified'}</p>
              </div>
            </div>
            
            {/* Additional Lead Details */}
            {(lead.annuity_type || lead.annuity_type_preference || lead.term_length) && (
              <div className="mt-6 p-4 border border-border-primary rounded-lg bg-background-tertiary">
                <h3 className="font-medium mb-4 text-text-primary">Annuity Preferences</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {lead.annuity_type && (
                    <div>
                      <p className="text-sm text-text-secondary mb-1">Annuity Type</p>
                      <p className="font-medium text-text-primary">{lead.annuity_type}</p>
                    </div>
                  )}
                  {lead.annuity_type_preference && (
                    <div>
                      <p className="text-sm text-text-secondary mb-1">Type Preference</p>
                      <p className="font-medium text-text-primary">{lead.annuity_type_preference}</p>
                    </div>
                  )}
                  {lead.term_length && (
                    <div>
                      <p className="text-sm text-text-secondary mb-1">Term Length</p>
                      <p className="font-medium text-text-primary">{lead.term_length}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      
      {/* Payment Link Section */}
      <Card>
        <CardHeader 
          className={`pb-3 cursor-pointer hover:bg-background-tertiary ${expandedSections.payment ? '' : 'border-b'}`}
          onClick={() => toggleSection('payment')}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-border-primary" />
              Payment Link
            </CardTitle>
            {expandedSections.payment ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        
        {expandedSections.payment && (
          <CardContent>
            <div className="space-y-6">
              {/* Price Information */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                  <p className="text-sm font-medium text-text-secondary">Current Price</p>
                  <p className="text-2xl font-bold text-status-info-text">
                    {formatCurrency(lead.price || 0)}
                  </p>
                </div>
                
                <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                  <p className="text-sm font-medium text-text-secondary">Investment Amount</p>
                  <p className="text-xl font-semibold text-text-primary">
                    {typeof lead.investment_amount === 'string' && lead.investment_amount.includes('$') 
                      ? lead.investment_amount 
                      : formatCurrency(lead.investment_amount || 0)}
                  </p>
                </div>
                
                <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                  <p className="text-sm font-medium text-text-secondary">Status</p>
                  <div className="mt-1">
                    {lead.is_purchased ? (
                      <Badge variant="success" className="text-sm py-1 px-2">Purchased</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-sm py-1 px-2">Available</Badge>
                    )}
                    {lead.stripe_payment_link && (
                      <Badge variant="outline" className="ml-2 text-sm py-1 px-2">Link Active</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Payment Link */}
              <div className="mt-4 bg-background-tertiary p-4 rounded-lg border border-border-primary">
                <h3 className="font-medium text-text-primary mb-3">Payment Link</h3>
                
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={lead.stripe_payment_link || lead.payment_link || 'No payment link generated'}
                      readOnly
                      className="w-full font-mono text-sm bg-background-secondary"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(lead.stripe_payment_link || lead.payment_link)}
                      disabled={!(lead.stripe_payment_link || lead.payment_link)}
                      title="Copy to clipboard"
                    >
                      {copySuccess ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(lead.stripe_payment_link || lead.payment_link, '_blank')}
                      disabled={!(lead.stripe_payment_link || lead.payment_link)}
                      title="Open payment page"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex gap-2 mt-2">
                    <Button 
                      variant="default"
                      className="bg-button-primary-bg hover:bg-button-primary-hover" 
                      onClick={handleGeneratePaymentLink}
                      disabled={generatingLink}
                    >
                      {generatingLink ? (
                        <>
                          <Spinner className="mr-2 h-4 w-4" />
                          {lead.stripe_payment_link ? 'Regenerating Link...' : 'Generating Link...'}
                        </>
                      ) : (
                        <>
                          {lead.stripe_payment_link ? 'Regenerate Payment Link' : 'Generate Payment Link'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Purchase Info */}
                {lead.is_purchased && (
                  <Alert className="mt-4 bg-status-success-bg border-status-success-border">
                    <AlertDescription className="text-status-success-text">
                      <div className="font-medium">Purchased by {lead.agent_email || "an agent"}</div>
                      <div className="text-sm mt-1">
                        {lead.purchase_date ? `Purchased on ${new Date(lead.purchase_date).toLocaleString()}` : ''}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Pricing Tiers Table */}
                <div className="mt-4 p-3 border border-border-primary rounded-md">
                  <h4 className="font-medium text-sm mb-2 text-text-primary">Price Tiers</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-background-tertiary">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-text-primary">Investment Amount Range</th>
                          <th className="text-right px-3 py-2 font-medium text-text-primary">Lead Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-primary">
                        <tr>
                          <td className="px-3 py-2 text-text-primary">$25,000 - $100,000</td>
                          <td className="text-right px-3 py-2 text-text-primary">$150</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-text-primary">$100,001 - $250,000</td>
                          <td className="text-right px-3 py-2 text-text-primary">$225</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-text-primary">$250,001 - $500,000</td>
                          <td className="text-right px-3 py-2 text-text-primary">$275</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-text-primary">$500,001 - $1,000,000</td>
                          <td className="text-right px-3 py-2 text-text-primary">$325</td>
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-text-primary">Over $1,000,000</td>
                          <td className="text-right px-3 py-2 text-text-primary">$400</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Agents Section (always collapsed by default) */}
      <Card>
        <CardHeader 
          className={`pb-3 cursor-pointer hover:bg-background-tertiary ${expandedSections.agents ? '' : 'border-b'}`}
          onClick={() => toggleSection('agents')}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-border-primary" />
              Agents in {stateName} ({agents.length})
            </CardTitle>
            {expandedSections.agents ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        
        {expandedSections.agents && (
          <CardContent>
            {loadingAgents ? (
              <div className="flex justify-center py-12">
                <Spinner className="h-8 w-8 text-border-primary" />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <User className="h-12 w-12 mx-auto text-text-tertiary" />
                <h3 className="mt-4 text-lg font-medium text-text-primary">No Agents Found</h3>
                <p className="mt-1 text-text-secondary">No agents registered for {stateName}</p>
              </div>
            ) : (
              <div>
                <div className="overflow-x-auto rounded-md border border-border-primary">
                  <table className="w-full text-sm">
                    <thead className="bg-background-tertiary">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Email</th>
                        <th className="px-4 py-2 text-right font-medium text-text-primary">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary">
                      {agents.slice(0, 5).map((agent) => (
                        <tr key={agent.id} className="hover:bg-background-tertiary">
                          <td className="px-4 py-2 font-medium text-text-primary">{agent.name || 'Unknown'}</td>
                          <td className="px-4 py-2 text-text-primary">{agent.email}</td>
                          <td className="px-4 py-2 text-right">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleSendEmail(agent.email)}
                              disabled={sendingEmails}
                            >
                              {sendingEmails ? (
                                <>
                                  <Spinner className="mr-2 h-3 w-3" />
                                  Sending...
                                </>
                              ) : (
                                'Send Email'
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {agents.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAgentsModal(true)}
                    >
                      View All {agents.length} Agents
                    </Button>
                  </div>
                )}
                
                <div className="mt-6 flex justify-between">
                  <p className="text-sm text-text-secondary">
                    {agents.length} agent{agents.length !== 1 ? 's' : ''} in {stateName}
                  </p>
                  <Button 
                    onClick={handleEmailPreview} 
                    disabled={sendingEmails || agents.length === 0}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Preview & Send Initial Email
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
      
      {/* Email History Section (always collapsed by default) */}
      <Card>
        <CardHeader 
          className={`pb-3 cursor-pointer hover:bg-background-tertiary ${expandedSections.emailHistory ? '' : 'border-b'}`}
          onClick={() => toggleSection('emailHistory')}
        >
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <MailOpen className="h-5 w-5 text-border-primary" />
              Email History
            </CardTitle>
            {expandedSections.emailHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </CardHeader>
        
        {expandedSections.emailHistory && (
          <CardContent>
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Spinner className="h-8 w-8" />
                <span className="ml-2 text-text-secondary">Loading email history...</span>
              </div>
            ) : emailHistory.length === 0 ? (
              <div className="text-center py-8">
                <MailOpen className="h-12 w-12 mx-auto text-text-tertiary" />
                <h3 className="mt-2 text-lg font-medium text-text-primary">No Email History</h3>
                <p className="mt-1 text-text-secondary">No emails have been sent for this lead yet.</p>
              </div>
            ) : (
              <div>
                <div className="overflow-x-auto rounded-md border border-border-primary">
                  <table className="w-full text-sm">
                    <thead className="bg-background-tertiary">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Date Sent</th>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Agent</th>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Email Type</th>
                        <th className="px-4 py-2 text-left font-medium text-text-primary">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary">
                      {emailHistory.slice(0, 5).map((email, index) => (
                        <tr key={index} className="hover:bg-background-tertiary">
                          <td className="px-4 py-2 text-text-primary">{new Date(email.sent_at).toLocaleString()}</td>
                          <td className="px-4 py-2 text-text-primary">{email.agent_email}</td>
                          <td className="px-4 py-2">
                            <Badge variant={email.email_type === 'initial_notification' ? 'outline' : 'secondary'}>
                              {email.email_type === 'initial_notification' ? 'Initial Notification' : 
                               email.email_type === 'full_details' ? 'Full Details' : 
                               email.email_type || 'Unknown'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2">
                            <Badge 
                              variant={
                                email.status === 'delivered' ? 'success' :
                                email.status === 'opened' ? 'info' :
                                email.status === 'clicked' ? 'warning' :
                                email.status === 'failed' ? 'destructive' :
                                'default'
                              }
                            >
                              {email.status || 'sent'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {emailHistory.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowEmailHistoryModal(true)}
                    >
                      View All {emailHistory.length} Email Records
                    </Button>
                  </div>
                )}
                
                {/* Summary Metrics */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                    <p className="text-sm text-text-secondary">Initial Notifications</p>
                    <p className="text-2xl font-semibold text-text-primary">
                      {emailHistory.filter(email => email.email_type === 'initial_notification').length}
                    </p>
                  </div>
                  <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                    <p className="text-sm text-text-secondary">Full Details</p>
                    <p className="text-2xl font-semibold text-text-primary">
                      {emailHistory.filter(email => email.email_type === 'full_details').length}
                    </p>
                  </div>
                  <div className="bg-background-secondary p-4 rounded-md border border-border-primary shadow-sm">
                    <p className="text-sm text-text-secondary">Success Rate</p>
                    <p className="text-2xl font-semibold text-text-primary">
                      {emailHistory.length ? Math.round((emailHistory.filter(email => email.status !== 'failed').length / emailHistory.length) * 100) : 0}%
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      fetchEmailHistory();
                      setSuccess('Email history refreshed');
                    }}
                    disabled={loadingHistory}
                  >
                    Refresh History
                  </Button>
                </div>
              </div>
            )}
            
            {lead?.is_purchased && (
              <div className="mt-8 p-4 border border-status-success-border bg-status-success-bg rounded-md">
                <h3 className="text-lg font-medium text-status-success-text flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  Lead Purchased
                </h3>
                <p className="mt-2 text-status-success-text">
                  This lead was purchased by <strong>{lead.agent_email || 'an agent'}</strong>.
                  Full contact details have been sent.
                </p>
                {lead.purchase_date && (
                  <p className="mt-1 text-sm text-status-success-text">
                    Purchased on: {new Date(lead.purchase_date).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
      
      {/* Email Preview Modal */}
      <Dialog open={showEmailPreviewModal} onOpenChange={setShowEmailPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          {loadingPreview ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="border border-border-primary rounded-lg p-4 bg-background-secondary my-4">
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
            </div>
          )}
          <DialogFooter className="flex items-center justify-between mt-4">
            <Button variant="outline" onClick={() => setShowEmailPreviewModal(false)} disabled={sendingToAllAgents}>Cancel</Button>
            <Button onClick={handleSendToAllAgents} disabled={sendingToAllAgents || agents.length === 0}>
              {sendingToAllAgents ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Sending to {agents.length} agents...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send to {agents.length} Agent{agents.length === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Agents Modal */}
      <Dialog open={showAgentsModal} onOpenChange={setShowAgentsModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agents in {stateName}</DialogTitle>
          </DialogHeader>
          {loadingAgents ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border-primary my-4">
              <table className="w-full text-sm">
                <thead className="bg-background-tertiary">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Name</th>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Email</th>
                    <th className="px-4 py-2 text-right font-medium text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-primary">
                  {agents.map((agent) => (
                    <tr key={agent.id} className="hover:bg-background-tertiary">
                      <td className="px-4 py-2 font-medium text-text-primary">{agent.name || 'Unknown'}</td>
                      <td className="px-4 py-2 text-text-primary">{agent.email}</td>
                      <td className="px-4 py-2 text-right">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            handleSendEmail(agent.email);
                            setShowAgentsModal(false);
                          }}
                          disabled={sendingEmails}
                        >
                          {sendingEmails ? (
                            <>
                              <Spinner className="mr-2 h-3 w-3" />
                              Sending...
                            </>
                          ) : (
                            'Send Email'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAgentsModal(false)}>
              Close
            </Button>
            <Button onClick={handleEmailPreview} disabled={agents.length === 0}>
              <Send className="mr-2 h-4 w-4" />
              Preview & Send to All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email History Modal */}
      <Dialog open={showEmailHistoryModal} onOpenChange={setShowEmailHistoryModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Email History</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  fetchEmailHistory();
                  setSuccess('Email history refreshed');
                }}
                disabled={loadingHistory}
              >
                Refresh
              </Button>
            </DialogTitle>
          </DialogHeader>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
              <span className="ml-2 text-text-secondary">Loading email history...</span>
            </div>
          ) : emailHistory.length === 0 ? (
            <div className="text-center py-8">
              <MailOpen className="h-12 w-12 mx-auto text-text-tertiary" />
              <h3 className="mt-2 text-lg font-medium text-text-primary">No Email History</h3>
              <p className="mt-1 text-text-secondary">No emails have been sent for this lead yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border-primary my-4">
              <table className="w-full text-sm">
                <thead className="bg-background-tertiary">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Date Sent</th>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Agent</th>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Email Type</th>
                    <th className="px-4 py-2 text-left font-medium text-text-primary">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-primary">
                  {emailHistory.map((email, index) => (
                    <tr key={index} className="hover:bg-background-tertiary">
                      <td className="px-4 py-2 text-text-primary">{new Date(email.sent_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-text-primary">{email.agent_email}</td>
                      <td className="px-4 py-2">
                        <Badge variant={email.email_type === 'initial_notification' ? 'outline' : 'secondary'}>
                          {email.email_type === 'initial_notification' ? 'Initial Notification' : 
                           email.email_type === 'full_details' ? 'Full Details' : 
                           email.email_type || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Badge 
                          variant={
                            email.status === 'delivered' ? 'success' :
                            email.status === 'opened' ? 'info' :
                            email.status === 'clicked' ? 'warning' :
                            email.status === 'failed' ? 'destructive' :
                            'default'
                          }
                        >
                          {email.status || 'sent'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailHistoryModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageLeadEmails;